import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A Great War biplane fighter parked on the grass: two stacked doped-fabric
// wings joined by wooden interplane and cabane struts, an open cockpit behind
// a round engine cowl, a fixed two-wheel undercarriage, a tail skid and a big
// two-blade wooden propeller. Roundel discs sit on the wings. The propeller
// parts are named so the looping "propeller-spin" animation can turn them
// about the hub. Faces +Z (nose north), spans a 2x2 pad.

const KHAKI = "#7a6f4e";
const CANVAS = "#cbbd93";
const WOOD = "#7a4a30";
const GUNMETAL = "#4a4d52";
const TYRE = "#33342f";

const FUS_Y = 0.32; // fuselage centreline height
const NOSE_Z = 0.64; // front of the cowl
const HUB: [number, number, number] = [0, FUS_Y, 0.74]; // propeller hub

function box(
  w: number, h: number, d: number, color: string,
  position: [number, number, number], rotation?: [number, number, number],
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation };
}

function cyl(
  r: number, h: number, seg: number, color: string,
  position: [number, number, number], rotation?: [number, number, number],
): Part {
  return { geometry: new THREE.CylinderGeometry(r, r, h, seg), color, position, rotation };
}

// A roundel: a light disc with a smaller dark disc on top, lying flat on a wing.
function roundel(x: number, y: number, z: number): Part[] {
  return [
    cyl(0.10, 0.015, 14, CANVAS, [x, y, z]),
    cyl(0.055, 0.015, 12, WOOD, [x, y + 0.008, z]),
  ];
}

function parts(): Part[] {
  const out: Part[] = [
    // fuselage: forward box, tapering rear boom, round engine cowl
    box(0.18, 0.18, 0.86, KHAKI, [0, FUS_Y, 0.18]),
    box(0.13, 0.13, 0.58, KHAKI, [0, 0.31, -0.46]),
    cyl(0.105, 0.16, 12, GUNMETAL, [0, FUS_Y, NOSE_Z], [Math.PI / 2, 0, 0]),

    // open cockpit: dark opening + raked windscreen
    box(0.12, 0.03, 0.16, "#22231f", [0, 0.42, 0.06]),
    box(0.12, 0.05, 0.015, CANVAS, [0, 0.445, 0.155], [-0.35, 0, 0]),

    // lower wing (through the fuselage) and larger, slightly staggered upper wing
    box(1.60, 0.03, 0.34, KHAKI, [0, 0.25, 0.12]),
    box(1.84, 0.03, 0.36, KHAKI, [0, 0.66, 0.16]),

    // interplane struts (outboard) and cabane struts (over the fuselage)
    box(0.03, 0.40, 0.03, WOOD, [0.62, 0.455, 0.03]),
    box(0.03, 0.40, 0.03, WOOD, [0.62, 0.455, 0.25]),
    box(0.03, 0.40, 0.03, WOOD, [-0.62, 0.455, 0.03]),
    box(0.03, 0.40, 0.03, WOOD, [-0.62, 0.455, 0.25]),
    box(0.03, 0.26, 0.03, WOOD, [0.12, 0.53, 0.05]),
    box(0.03, 0.26, 0.03, WOOD, [0.12, 0.53, 0.25]),
    box(0.03, 0.26, 0.03, WOOD, [-0.12, 0.53, 0.05]),
    box(0.03, 0.26, 0.03, WOOD, [-0.12, 0.53, 0.25]),

    // roundels: on top of the upper wing and on top of the lower wing
    ...roundel(0.62, 0.683, 0.16),
    ...roundel(-0.62, 0.683, 0.16),
    ...roundel(0.55, 0.273, 0.12),
    ...roundel(-0.55, 0.273, 0.12),

    // tail: stabiliser, fin and canvas rudder
    box(0.56, 0.025, 0.22, KHAKI, [0, 0.31, -0.72]),
    box(0.025, 0.21, 0.20, KHAKI, [0, 0.42, -0.70]),
    // rudder: thicker than the fin so no faces are coplanar
    box(0.04, 0.26, 0.10, CANVAS, [0, 0.42, -0.83]),

    // fixed undercarriage: two wheels on an axle, held by V struts
    cyl(0.09, 0.05, 12, TYRE, [0.18, 0.09, 0.30], [0, 0, Math.PI / 2]),
    cyl(0.09, 0.05, 12, TYRE, [-0.18, 0.09, 0.30], [0, 0, Math.PI / 2]),
    cyl(0.014, 0.36, 6, GUNMETAL, [0, 0.09, 0.30], [0, 0, Math.PI / 2]),
    box(0.025, 0.20, 0.025, WOOD, [0.10, 0.165, 0.25], [0.30, 0, 0.42]),
    box(0.025, 0.20, 0.025, WOOD, [0.10, 0.165, 0.35], [-0.30, 0, 0.42]),
    box(0.025, 0.20, 0.025, WOOD, [-0.10, 0.165, 0.25], [0.30, 0, -0.42]),
    box(0.025, 0.20, 0.025, WOOD, [-0.10, 0.165, 0.35], [-0.30, 0, -0.42]),

    // tail skid reaching down to the ground
    box(0.03, 0.24, 0.04, WOOD, [0, 0.13, -0.70], [0.35, 0, 0]),

    // the two-blade wooden propeller: hub + one long blade bar, both named
    { geometry: new THREE.CylinderGeometry(0.032, 0.032, 0.07, 8), color: GUNMETAL,
      position: HUB, rotation: [Math.PI / 2, 0, 0], name: "propeller" },
    { geometry: new THREE.BoxGeometry(0.05, 0.64, 0.025), color: WOOD,
      position: [HUB[0], HUB[1], HUB[2] + 0.01], rotation: [0, 0, 0.5], name: "propeller" },
  ];
  return out;
}

export const biplaneFighter = defineModel({
  id: "biplane-fighter",
  name: "Biplane Fighter",
  tags: ["vehicle", "wwi", "military", "aircraft", "biplane", "animated", "low-poly"],
  batchId: "wwi",
  summary: "A parked Great War biplane fighter with a spinning propeller.",
  description:
    "A single-seat Great War biplane fighter sitting on the ground: two " +
    "stacked khaki wings with wooden interplane and cabane struts, roundel " +
    "discs on the wings, an open cockpit behind a gunmetal cowl, fixed " +
    "two-wheel undercarriage with a tail skid, and a big two-blade wooden " +
    "propeller driven by the looping propeller-spin animation. Faces +Z, " +
    "parks on a 2x2 pad.",
  author: "Fable 5",
  footprint: 0.95,
  tiles: { w: 2, h: 2 },
  parts,
  animations: [
    {
      id: "propeller-spin",
      label: "Propeller spinning",
      duration: 0.6,
      loop: true,
      tracks: [
        {
          target: "propeller",
          pivot: HUB,
          rotation: [
            { t: 0, value: [0, 0, 0] },
            { t: 0.6, value: [0, 0, -Math.PI * 2] },
          ],
        },
      ],
    },
  ],
});
